import React, { Suspense, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import { styled } from '@mui/system';

import Header from './pages/components/Header';

const PageNotFound = React.lazy(() => import('./pages/404'));
const Login = React.lazy(() => import('./pages/Login'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const WebsiteAnnouncementList = React.lazy(() => import('./pages/website/announcement/WebsiteAnnouncementList'));
const WebsiteAnnouncementForm = React.lazy(() => import('./pages/website/announcement/WebsiteAnnouncementForm'));
const SettingsBrowser = React.lazy(() => import('./pages/settings/SettingsBrowser'));
const NPCList = React.lazy(() => import('./pages/npc/NPCList'));
const NPCForm = React.lazy(() => import('./pages/npc/NPCForm'));
const UserList = React.lazy(() => import('./pages/user/UserList'));
const UserForm = React.lazy(() => import('./pages/user/UserForm'));
import 'react-toastify/dist/ReactToastify.css';
const OperationList = React.lazy(() => import('./pages/operation/OperationList'));
const OperationForm = React.lazy(() => import('./pages/operation/OperationForm'));
const DashboardJournalList = React.lazy(() => import('./pages/dashboardjournal/DashboardJournalList'));
const DiscordBotSettings = React.lazy(() => import('./pages/discord/DiscordBotSettings'));
const JobList = React.lazy(() => import('./pages/job/JobList'));
const JobForm = React.lazy(() => import('./pages/job/JobForm'));
const PlayerDetails = React.lazy(() => import('./pages/player/PlayerDetails'));
const TransactionSearch = React.lazy(() => import('./pages/economy/TransactionSearch'));
const BankAccountSearch = React.lazy(() => import('./pages/economy/bank/BankAccountSearch'));
const FeatureFlagList = React.lazy(() => import('./pages/featureflag/FeatureFlagList'));
const I18nList = React.lazy(() => import('./pages/i18n/I18nList'));
const BlockedPhraseList = React.lazy(() => import('./pages/blockedphrases/BlockedPhraseList'));
const ServerRuleList = React.lazy(() => import('./pages/serverrule/ServerRuleList'));
const GameAnnouncementList = React.lazy(() => import('./pages/gameannouncement/GameAnnouncementList'));
const FactionList = React.lazy(() => import('./pages/faction/FactionList'));
const FactionForm = React.lazy(() => import('./pages/faction/FactionForm'));
const HorseShoeList = React.lazy(() => import('./pages/horseshoe/HorseShoeList'));
const HorseShoeForm = React.lazy(() => import('./pages/horseshoe/HorseShoeForm'));
const HorseBitList = React.lazy(() => import('./pages/horsebit/HorseBitList'));
const HorseBitForm = React.lazy(() => import('./pages/horsebit/HorseBitForm'));
const DiscoverLocationList = React.lazy(() => import('./pages/discoverlocation/DiscoverLocationList'));
const DiscoverLocationForm = React.lazy(() => import('./pages/discoverlocation/DiscoverLocationForm'));
import { PlayerLocationProvider } from './playerlocation/PlayerLocation';
const QuestRoutes = React.lazy(() => import('./pages/quest'));
const WebsitePageTextList = React.lazy(() => import('./pages/website/pagetext/WebsitePageTextList'));
const WebsitePageTextForm = React.lazy(() => import('./pages/website/pagetext/WebsitePageTextForm'));
const WebsiteLinkList = React.lazy(() => import('./pages/website/link/WebsiteLinkList'));
const WebsiteLinkForm = React.lazy(() => import('./pages/website/link/WebsiteLinkForm'));
const WebsiteApplicationTypeList = React.lazy(() => import('./pages/website/application/type/WebsiteApplicationTypeList'));
const WebsiteApplicationTypeForm = React.lazy(() => import('./pages/website/application/type/WebsiteApplicationTypeForm'));
const WebsiteApplicationResponseTypeSelect = React.lazy(() => import('./pages/website/application/response/WebsiteApplicationResponseTypeSelect'));
const WebsiteApplicationResponseList = React.lazy(() => import('./pages/website/application/response/WebsiteApplicationResponseList'));
const WebsiteApplicationResponseDetail = React.lazy(() => import('./pages/website/application/response/WebsiteApplicationResponseDetail'));
const IllnessSymptomList = React.lazy(() => import('./pages/illnesssymptom/IllnessSymptomList'));
const IllnessSymptomForm = React.lazy(() => import('./pages/illnesssymptom/IllnessSymptomForm'));
const IllnessList = React.lazy(() => import('./pages/illness/IllnessList'));
const IllnessForm = React.lazy(() => import('./pages/illness/IllnessForm'));
const DisciplineList = React.lazy(() => import('./pages/discipline/DisciplineList'));
const DisciplineForm = React.lazy(() => import('./pages/discipline/DisciplineForm'));
import BreadcrumbTrail from './pages/components/BreadcrumbTrail';
const LeaderboardForm = React.lazy(() => import('./pages/leaderboard/LeaderboardForm'));
const LeaderboardList = React.lazy(() => import('./pages/leaderboard/LeaderboardList'));
const ConformationMeasurementList = React.lazy(() => import('./pages/conformation/measurement/ConformationMeasurementList'));
const ConformationMeasurementForm = React.lazy(() => import('./pages/conformation/measurement/ConformationMeasurementForm'));
const HorseRegistrationOrganizationList = React.lazy(() => import('./pages/registrationorganization/HorseRegistrationOrganizationList'));
const HorseRegistrationOrganizationForm = React.lazy(() => import('./pages/registrationorganization/HorseRegistrationOrganizationForm'));
const HorseAttributeList = React.lazy(() => import('./pages/horseattribute/HorseAttributeList'));
const HorseAttributeForm = React.lazy(() => import('./pages/horseattribute/HorseAttributeForm'));
const NutrientList = React.lazy(() => import('./pages/nutrient/NutrientList'));
const NutrientForm = React.lazy(() => import('./pages/nutrient/NutrientForm'));
const FastTravelGroupList = React.lazy(() => import('./pages/fasttravel/group/FastTravelGroupList'));
const FastTravelGroupForm = React.lazy(() => import('./pages/fasttravel/group/FastTravelGroupForm'));
const FastTravelPointList = React.lazy(() => import('./pages/fasttravel/point/FastTravelPointList'));
const FastTravelPointForm = React.lazy(() => import('./pages/fasttravel/point/FastTravelPointForm'));
const JoinLeaveMessageList = React.lazy(() => import('./pages/joinleavemessage/JoinLeaveMessageList'));
const JoinLeaveMessageForm = React.lazy(() => import('./pages/joinleavemessage/JoinLeaveMessageForm'));
const SalesBarnGroupList = React.lazy(() => import('./pages/salesbarn/SalesBarnGroupList'));
const SalesBarnGroupForm = React.lazy(() => import('./pages/salesbarn/SalesBarnGroupForm'));
const PlotGlobalSettingsForm = React.lazy(() => import('./pages/plot/PlotGlobalSettingsForm'));
const DiscordBotServerList = React.lazy(() => import('./pages/discord/DiscordBotServerList'));
const DiscordBotServerForm = React.lazy(() => import('./pages/discord/DiscordBotServerForm'));
const UniversityModuleList = React.lazy(() => import('./pages/university/module/UniversityModuleList'));
const UniversityModuleForm = React.lazy(() => import('./pages/university/module/UniversityModuleForm'));
const UniversityDegreeList = React.lazy(() => import('./pages/university/degree/UniversityDegreeList'));
const UniversityDegreeForm = React.lazy(() => import('./pages/university/degree/UniversityDegreeForm'));
const UniversityHouseList = React.lazy(() => import('./pages/university/house/UniversityHouseList'));
const UniversityHouseForm = React.lazy(() => import('./pages/university/house/UniversityHouseForm'));
const PlayerAttributeList = React.lazy(() => import('./pages/playerattribute/PlayerAttributeList'));
const PlayerAttributeForm = React.lazy(() => import('./pages/playerattribute/PlayerAttributeForm'));
const SpawnPointList = React.lazy(() => import('./pages/spawnpoint/SpawnPointList'));
const SpawnPointForm = React.lazy(() => import('./pages/spawnpoint/SpawnPointForm'));
const WeatherConfiguration = React.lazy(() => import('./pages/weather/config/WeatherConfiguration'));
const HorseBonnetList = React.lazy(() => import('./pages/horsetack/bonnet/HorseBonnetList'));
const HorseBonnetForm = React.lazy(() => import('./pages/horsetack/bonnet/HorseBonnetForm'));
const HorseBootsList = React.lazy(() => import('./pages/horsetack/boots/HorseBootsList'));
const HorseBootsForm = React.lazy(() => import('./pages/horsetack/boots/HorseBootsForm'));
const HorseBridleList = React.lazy(() => import('./pages/horsetack/bridle/HorseBridleList'));
const HorseBridleForm = React.lazy(() => import('./pages/horsetack/bridle/HorseBridleForm'));
const HorseCinchList = React.lazy(() => import('./pages/horsetack/cinch/HorseCinchList'));
const HorseCinchForm = React.lazy(() => import('./pages/horsetack/cinch/HorseCinchForm'));
const HorseGirthList = React.lazy(() => import('./pages/horsetack/girth/HorseGirthList'));
const HorseGirthForm = React.lazy(() => import('./pages/horsetack/girth/HorseGirthForm'));
const HorseHalterForm = React.lazy(() => import('./pages/horsetack/halter/HorseHalterForm'));
const HorseHalterList = React.lazy(() => import('./pages/horsetack/halter/HorseHalterList'));
const HorsePadList = React.lazy(() => import('./pages/horsetack/pad/HorsePadList'));
const HorsePadForm = React.lazy(() => import('./pages/horsetack/pad/HorsePadForm'));
const HorseHalfPadList = React.lazy(() => import('./pages/horsetack/halfpad/HorseHalfPadList'));
const HorseHalfPadForm = React.lazy(() => import('./pages/horsetack/halfpad/HorseHalfPadForm'));
const HorseShapedPadList = React.lazy(() => import('./pages/horsetack/shapedpad/HorseShapedPadList'));
const HorseShapedPadForm = React.lazy(() => import('./pages/horsetack/shapedpad/HorseShapedPadForm'));
const HorseReinList = React.lazy(() => import('./pages/horsetack/rein/HorseReinList'));
const HorseReinForm = React.lazy(() => import('./pages/horsetack/rein/HorseReinForm'));
const HorseSaddleList = React.lazy(() => import('./pages/horsetack/saddle/HorseSaddleList'));
const HorseSaddleForm = React.lazy(() => import('./pages/horsetack/saddle/HorseSaddleForm'));
const HorseWrapsList = React.lazy(() => import('./pages/horsetack/wraps/HorseWrapsList'));
const HorseWrapsForm = React.lazy(() => import('./pages/horsetack/wraps/HorseWrapsForm'));
const BulletinList = React.lazy(() => import('./pages/bulletin/BulletinList'));
const BulletinForm = React.lazy(() => import('./pages/bulletin/BulletinForm'));
const AlleleList = React.lazy(() => import('./pages/allele/AlleleList'));
const AlleleForm = React.lazy(() => import('./pages/allele/AlleleForm'));
const BusinessCategoryList = React.lazy(() => import('./pages/businesscategory/BusinessCategoryList'));
const BusinessCategoryForm = React.lazy(() => import('./pages/businesscategory/BusinessCategoryForm'));
const ChatTagList = React.lazy(() => import('./pages/chattag/ChatTagList'));
const ChatTagForm = React.lazy(() => import('./pages/chattag/ChatTagForm'));
const ShopList = React.lazy(() => import('./pages/shop/ShopList'));
const ShopForm = React.lazy(() => import('./pages/shop/ShopForm'));
const CollectableGroupList = React.lazy(() => import('./pages/collectable/group/CollectableGroupList'));
const CollectableGroupForm = React.lazy(() => import('./pages/collectable/group/CollectableGroupForm'));
const CollectableItemList = React.lazy(() => import('./pages/collectable/item/CollectableItemList'));
const CollectableItemForm = React.lazy(() => import('./pages/collectable/item/CollectableItemForm'));
const BloodAgeSettingList = React.lazy(() => import('./pages/blood/age/BloodAgeSettingList'));
const BloodAgeSettingForm = React.lazy(() => import('./pages/blood/age/BloodAgeSettingForm'));
const WarningReasonList = React.lazy(() => import('./pages/warning/reason/WarningReasonList'));
const WarningReasonForm = React.lazy(() => import('./pages/warning/reason/WarningReasonForm'));
const WarningList = React.lazy(() => import('./pages/warning/warning/WarningList'));
const StudRegistryList = React.lazy(() => import('./pages/studregistry/StudRegistryList'));
const StudRegistryForm = React.lazy(() => import('./pages/studregistry/StudRegistryForm'));
const HorseGaitList = React.lazy(() => import('./pages/horsegait/HorseGaitList'));
const UniversityBookList = React.lazy(() => import('./pages/university/book/UniversityBookList'));
const UniversityBookForm = React.lazy(() => import('./pages/university/book/UniversityBookForm'));
const SkillList = React.lazy(() => import('./pages/skill/SkillList'));
const SkillForm = React.lazy(() => import('./pages/skill/SkillForm'));
const EconomyOverview = React.lazy(() => import('./pages/economy/EconomyOverview'));
import { StatusProvider } from './status/StatusContext';
const AdminTasks = React.lazy(() => import('./pages/admin/AdminTasks'));
const UnscrambleWordList = React.lazy(() => import('./pages/unscramble/UnscrambleWordList'));
const DailyJoinRewardList = React.lazy(() => import('./pages/dailyjoin/DailyJoinRewardList'));
const DailyJoinRewardForm = React.lazy(() => import('./pages/dailyjoin/DailyJoinRewardForm'));
const NameGeneratorList = React.lazy(() => import('./pages/namegenerator/NameGeneratorList'));
const DashboardRoleList = React.lazy(() => import('./pages/dashboardrole/DashboardRoleList'));
const DashboardRoleForm = React.lazy(() => import('./pages/dashboardrole/DashboardRoleForm'));
import { useAuthToken } from './user/UserUtil';
import { loadFeatureFlags } from './featureflag/FeatureFlagUtil';
const PlayerRankList = React.lazy(() => import('./pages/playerrank/PlayerRankList'));
const PlayerRankForm = React.lazy(() => import('./pages/playerrank/PlayerRankForm'));
const BloodTypeForm = React.lazy(() => import('./pages/blood/type/BloodTypeForm'));
const BloodTypeList = React.lazy(() => import('./pages/blood/type/BloodTypeList'));
const PlayerClubPlusImport = React.lazy(() => import('./pages/player/PlayerClubPlusImport'));
const PlayerDonatorRankImport = React.lazy(() => import('./pages/player/PlayerDonatorRankImport'));
const PlayerDonatorImport = React.lazy(() => import('./pages/player/PlayerDonatorImport'));
const WeatherForecast = React.lazy(() => import('./pages/weather/WeatherForecast'));
const PlayerJoinDateImport = React.lazy(() => import('./pages/player/PlayerJoinDateImport'));
const BreedList = React.lazy(() => import('./pages/breed/BreedList'));
const BreedForm = React.lazy(() => import('./pages/breed/BreedForm'));
const BreedGroupList = React.lazy(() => import('./pages/breed/group/BreedGroupList'));
const BreedGroupForm = React.lazy(() => import('./pages/breed/group/BreedGroupForm'));
const AgeSettingList = React.lazy(() => import('./pages/agesetting/AgeSettingList'));
const AgeSettingForm = React.lazy(() => import('./pages/agesetting/AgeSettingForm'));
const RoadmapList = React.lazy(() => import('./pages/roadmap/RoadmapList'));
const RoadmapForm = React.lazy(() => import('./pages/roadmap/RoadmapForm'));
const NavigatorItemList = React.lazy(() => import('./pages/navigator/NavigatorItemList'));
const NavigatorItemForm = React.lazy(() => import('./pages/navigator/NavigatorItemForm'));
const ChangePasswordPage = React.lazy(() => import('./pages/user/ChangePasswordPage'));
const CreateUserPage = React.lazy(() => import('./pages/user/CreateUserPage'));
import { Box, CircularProgress, Typography, keyframes } from '@mui/material';
import Navbar from './pages/components/Navbar';
import ErrorBoundary from './ErrorBoundary';
const ChatCommandLogs = React.lazy(() => import('./pages/chatcommandlogs/ChatCommandLogs'));
const PlayerReportList = React.lazy(() => import('./pages/playerreport/PlayerReportList'));
const DuplicateIPList = React.lazy(() => import("./pages/ipcheck/DuplicateIPList"));
const TagList = React.lazy(() => import("./pages/tag/TagList"));
const EconomyValues = React.lazy(() => import("./pages/economy/EconomyValues"));
const FeedbackList = React.lazy(() => import("./pages/feedback/FeedbackList"));
const PlayerExport = React.lazy(() => import("./pages/player/PlayerExport"));
const PlayerChatTagImport = React.lazy(() => import("./pages/player/PlayerChatTagImport"));
const PlayerTokensImport = React.lazy(() => import("./pages/player/PlayerTokensImport"));
const PlayerAdvancedTokenImport = React.lazy(() => import("./pages/player/PlayerAdvancedTokenImport"));
const PresetBuildList = React.lazy(() => import("./pages/preset/builds/PresetBuildList"));
const PresetBuildForm = React.lazy(() => import("./pages/preset/builds/PresetBuildForm"));
const PresetBuildOrderList = React.lazy(() => import("./pages/preset/order/PresetBuildOrderList"));
const FloatingTextList = React.lazy(() => import("./pages/floatingtext/FloatingTextList"));
const FloatingTextForm = React.lazy(() => import("./pages/floatingtext/FloatingTextForm"));
const WebsiteFileManager = React.lazy(() => import("./pages/website/file/WebsiteFileManager"));
const WebsiteCarouselManager = React.lazy(() => import("./pages/website/carousel/WebsiteCarouselManager"));
const WebsiteCoreImageManager = React.lazy(() => import("./pages/website/core/WebsiteCoreImageManager"));
const HorseList = React.lazy(() => import("./pages/horse/HorseList"));
const HorseForm = React.lazy(() => import("./pages/horse/HorseForm"));
const PlayerSearch = React.lazy(() => import("./pages/player/PlayerSearch"));
const Monitoring = React.lazy(() => import("./pages/monitoring/Monitoring"));
const BuilderServerConsole = React.lazy(() => import("./pages/builderserver/BuilderServerConsole"));
const Bans = React.lazy(() => import("./pages/ban/Bans"));
const FineList = React.lazy(() => import("./pages/fine/FineList"));
const XPLogViewer = React.lazy(() => import("./pages/xplog/XPLogViewer"));
const PregnancySearch = React.lazy(() => import("./pages/pregnancy/PregnancySearch"));
const I18nPrefixList = React.lazy(() => import("./pages/i18n/I18nPrefixList"));
const CrashReportsViewer = React.lazy(() => import("./pages/crashreports/CrashReportsViewer"));
const ForgeLogsViewer = React.lazy(() => import("./pages/forge/ForgeLogsViewer"));
const DoubleXpManager = React.lazy(() => import("./pages/doublexp/DoubleXpManager"));
const BadgeList = React.lazy(() => import("./pages/badge/BadgeList"));
const BadgeForm = React.lazy(() => import("./pages/badge/BadgeForm"));
const TokenBalanceSearch = React.lazy(() => import("./pages/token/TokenBalanceSearch"));
const PlayerReferralRewardList = React.lazy(() => import("./pages/playerreferral/PlayerReferralRewardList"));
const MedicineList = React.lazy(() => import("./pages/medicine/MedicineList"));
const MedicineForm = React.lazy(() => import("./pages/medicine/MedicineForm"));
const MedicineOverdoseGroupList = React.lazy(() => import("./pages/medicineoverdosegroup/MedicineOverdoseGroupList"));
const MedicineOverdoseGroupForm = React.lazy(() => import("./pages/medicineoverdosegroup/MedicineOverdoseGroupForm"));
const ProvisionList = React.lazy(() => import("./pages/provision/ProvisionList"));
const ProvisionForm = React.lazy(() => import("./pages/provision/ProvisionForm"));
const CoatTextureList = React.lazy(() => import("./pages/coat/CoatTextureList"));
const CoatTextureForm = React.lazy(() => import("./pages/coat/CoatTextureForm"));
const PersonalityList = React.lazy(() => import("./pages/personality/PersonalityList"));
const PersonalityForm = React.lazy(() => import("./pages/personality/PersonalityForm"));
const SupplementList = React.lazy(() => import("./pages/supplement/SupplementList"));
const SupplementForm = React.lazy(() => import("./pages/supplement/SupplementForm"));
const CreditList = React.lazy(() => import("./pages/credit/CreditList"));
const CreditForm = React.lazy(() => import("./pages/credit/CreditForm"));
const AppContainer = styled(Box)({
    height: '100dvh',
    display: 'flex',
    flexDirection: 'column',
});

const pulse = keyframes`
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
`;

const PageLoader = () => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            minHeight: '60vh',
            width: '100%',
            color: 'text.secondary',
        }}
    >
        <CircularProgress size={48} thickness={4} />
        <Typography
            variant="body2"
            sx={{ animation: `${pulse} 1.4s ease-in-out infinite`, letterSpacing: '0.05em' }}
        >
            Loading…
        </Typography>
    </Box>
);

const AppRoutes = () => {

    return (
        <Box sx={{
            flexGrow: 1,
            overflowY: "auto",
            paddingLeft: '5px',
            paddingRight: '5px',
        }}>
            <Suspense fallback={<PageLoader />}>
            <Routes>
                <Route index element={<Dashboard />} />
                <Route path='*' element={<PageNotFound />} />
                <Route path='/login' element={<Login />} />
                <Route path='/questdesigner/*' element={<QuestRoutes />} />
                <Route path='/website/announcement' element={<WebsiteAnnouncementList />} />
                <Route path='/website/announcement/:id' element={<WebsiteAnnouncementForm />} />
                <Route path='/breed/breed' element={<BreedList />} />
                <Route path='/breed/breed/:id' element={<BreedForm />} />
                <Route path='/breed/group' element={<BreedGroupList />} />
                <Route path='/breed/group/:id' element={<BreedGroupForm />} />
                <Route path='/npc' element={<NPCList />} />
                <Route path='/npc/:id' element={<NPCForm />} />
                <Route path='/settings' element={<SettingsBrowser />} />
                <Route path='/log/dashboard' element={<DashboardJournalList />} />
                <Route path='/user' element={<UserList />} />
                <Route path='/user/:username' element={<UserForm />} />
                <Route path='/operation' element={<OperationList />} />
                <Route path='/operation/:id' element={<OperationForm />} />
                <Route path='/discord/settings' element={<DiscordBotSettings />} />
                <Route path='/job' element={<JobList />} />
                <Route path='/job/:id' element={<JobForm />} />
                <Route path='/player/:id' element={<PlayerDetails />} />
                <Route path='/economy/transaction' element={<TransactionSearch />} />
                <Route path='/economy/accounts' element={<BankAccountSearch />} />
                <Route path='/economy/overview' element={<EconomyOverview />} />
                <Route path='/weather/forecast' element={<WeatherForecast />} />
                <Route path='/weather/configuration' element={<WeatherConfiguration />} />
                <Route path='/featureflag' element={<FeatureFlagList />} />
                <Route path='/i18n' element={<I18nList />} />
                <Route path='/blockedphrase' element={<BlockedPhraseList />} />
                <Route path='/serverrule' element={<ServerRuleList />} />
                <Route path='/gameannouncement' element={<GameAnnouncementList />} />
                <Route path='/faction' element={<FactionList />} />
                <Route path='/faction/:id' element={<FactionForm />} />
                <Route path='/horseshoe' element={<HorseShoeList />} />
                <Route path='/horseshoe/:id' element={<HorseShoeForm />} />
                <Route path='/horsebit' element={<HorseBitList />} />
                <Route path='/horsebit/:id' element={<HorseBitForm />} />
                <Route path='/discoverlocation' element={<DiscoverLocationList />} />
                <Route path='/discoverlocation/:id' element={<DiscoverLocationForm />} />
                <Route path='/website/link' element={<WebsiteLinkList />} />
                <Route path='/website/link/:id' element={<WebsiteLinkForm />} />
                <Route path='/website/pagetext' element={<WebsitePageTextList />} />
                <Route path='/website/pagetext/:id' element={<WebsitePageTextForm />} />
                <Route path='/website/application/type' element={<WebsiteApplicationTypeList />} />
                <Route path='/website/application/type/:id' element={<WebsiteApplicationTypeForm />} />
                <Route path='/website/application/response' element={<WebsiteApplicationResponseTypeSelect />} />
                <Route path='/website/application/response/:typeId' element={<WebsiteApplicationResponseList />} />
                <Route path='/website/application/response/:typeId/:id' element={<WebsiteApplicationResponseDetail />} />
                <Route path='/illnesssymptom' element={<IllnessSymptomList />} />
                <Route path='/illnesssymptom/:id' element={<IllnessSymptomForm />} />
                <Route path='/illness' element={<IllnessList />} />
                <Route path='/illness/:id' element={<IllnessForm />} />
                <Route path='/discipline' element={<DisciplineList />} />
                <Route path='/discipline/:id' element={<DisciplineForm />} />
                <Route path='/leaderboard' element={<LeaderboardList />} />
                <Route path='/leaderboard/:id' element={<LeaderboardForm />} />
                <Route path='/conformation/measurement' element={<ConformationMeasurementList />} />
                <Route path='/conformation/measurement/:id' element={<ConformationMeasurementForm />} />
                <Route path='/registrationorganization' element={<HorseRegistrationOrganizationList />} />
                <Route path='/registrationorganization/:id' element={<HorseRegistrationOrganizationForm />} />
                <Route path='/attribute/horse' element={<HorseAttributeList />} />
                <Route path='/attribute/horse/:id' element={<HorseAttributeForm />} />
                <Route path='/attribute/player' element={<PlayerAttributeList />} />
                <Route path='/attribute/player/:id' element={<PlayerAttributeForm />} />
                <Route path='/nutrient' element={<NutrientList />} />
                <Route path='/nutrient/:identifier' element={<NutrientForm />} />
                <Route path='/fasttravel/group' element={<FastTravelGroupList />} />
                <Route path='/fasttravel/group/:id' element={<FastTravelGroupForm />} />
                <Route path='/fasttravel/point' element={<FastTravelPointList />} />
                <Route path='/fasttravel/point/:id' element={<FastTravelPointForm />} />
                <Route path='/joinleavemessage' element={<JoinLeaveMessageList />} />
                <Route path='/joinleavemessage/:id' element={<JoinLeaveMessageForm />} />
                <Route path='/salesbarn' element={<SalesBarnGroupList />} />
                <Route path='/salesbarn/:id' element={<SalesBarnGroupForm />} />
                <Route path='/plotglobalsettings' element={<PlotGlobalSettingsForm />} />
                <Route path='/discord/server' element={<DiscordBotServerList />} />
                <Route path='/discord/server/:id' element={<DiscordBotServerForm />} />
                <Route path='/university/degree' element={<UniversityDegreeList />} />
                <Route path='/university/degree/:id' element={<UniversityDegreeForm />} />
                <Route path='/university/module' element={<UniversityModuleList />} />
                <Route path='/university/module/:id' element={<UniversityModuleForm />} />
                <Route path='/university/house' element={<UniversityHouseList />} />
                <Route path='/university/house/:id' element={<UniversityHouseForm />} />
                <Route path='/spawnpoint' element={<SpawnPointList />} />
                <Route path='/spawnpoint/:id' element={<SpawnPointForm />} />
                <Route path='/skill' element={<SkillList />} />
                <Route path='/skill/:id' element={<SkillForm />} />
                <Route path='/horse/tack/bonnet' element={<HorseBonnetList />} />
                <Route path='/horse/tack/bonnet/:id' element={<HorseBonnetForm />} />
                <Route path='/horse/tack/boots' element={<HorseBootsList />} />
                <Route path='/horse/tack/boots/:id' element={<HorseBootsForm />} />
                <Route path='/horse/tack/bridle' element={<HorseBridleList />} />
                <Route path='/horse/tack/bridle/:id' element={<HorseBridleForm />} />
                <Route path='/horse/tack/cinch' element={<HorseCinchList />} />
                <Route path='/horse/tack/cinch/:id' element={<HorseCinchForm />} />
                <Route path='/horse/tack/girth' element={<HorseGirthList />} />
                <Route path='/horse/tack/girth/:id' element={<HorseGirthForm />} />
                <Route path='/horse/tack/halter' element={<HorseHalterList />} />
                <Route path='/horse/tack/halter/:id' element={<HorseHalterForm />} />
                <Route path='/horse/tack/pad' element={<HorsePadList />} />
                <Route path='/horse/tack/pad/:id' element={<HorsePadForm />} />
                <Route path='/horse/tack/half-pad' element={<HorseHalfPadList />} />
                <Route path='/horse/tack/half-pad/:id' element={<HorseHalfPadForm />} />
                <Route path='/horse/tack/shaped-pad' element={<HorseShapedPadList />} />
                <Route path='/horse/tack/shaped-pad/:id' element={<HorseShapedPadForm />} />
                <Route path='/horse/tack/rein' element={<HorseReinList />} />
                <Route path='/horse/tack/rein/:id' element={<HorseReinForm />} />
                <Route path='/horse/tack/saddle' element={<HorseSaddleList />} />
                <Route path='/horse/tack/saddle/:id' element={<HorseSaddleForm />} />
                <Route path='/horse/tack/wraps' element={<HorseWrapsList />} />
                <Route path='/horse/tack/wraps/:id' element={<HorseWrapsForm />} />
                <Route path='/bulletin' element={<BulletinList />} />
                <Route path='/bulletin/:id' element={<BulletinForm />} />
                <Route path='/allele' element={<AlleleList />} />
                <Route path='/allele/:id' element={<AlleleForm />} />
                <Route path='/business/category' element={<BusinessCategoryList />} />
                <Route path='/business/category/:id' element={<BusinessCategoryForm />} />
                <Route path='/chattag' element={<ChatTagList />} />
                <Route path='/chattag/:id' element={<ChatTagForm />} />
                <Route path='/shop' element={<ShopList />} />
                <Route path='/shop/:id' element={<ShopForm />} />
                <Route path='/collectable/group' element={<CollectableGroupList />} />
                <Route path='/collectable/group/:id' element={<CollectableGroupForm />} />
                <Route path='/collectable/item' element={<CollectableItemList />} />
                <Route path='/collectable/item/:id' element={<CollectableItemForm />} />
                <Route path='/studregistry' element={<StudRegistryList />} />
                <Route path='/studregistry/:id' element={<StudRegistryForm />} />
                <Route path='/blood/age' element={<BloodAgeSettingList />} />
                <Route path='/blood/age/:id' element={<BloodAgeSettingForm />} />
                <Route path='/warning/reason' element={<WarningReasonList />} />
                <Route path='/warning/reason/:id' element={<WarningReasonForm />} />
                <Route path='/warning/warning' element={<WarningList />} />
                <Route path='/horsegait' element={<HorseGaitList />} />
                <Route path='/university/book' element={<UniversityBookList />} />
                <Route path='/university/book/:id' element={<UniversityBookForm />} />
                <Route path='/admin/tasks' element={<AdminTasks />} />
                <Route path='/unscramble' element={<UnscrambleWordList />} />
                <Route path='/dailyjoinreward' element={<DailyJoinRewardList />} />
                <Route path='/dailyjoinreward/:id' element={<DailyJoinRewardForm />} />
                <Route path='/namegenerator' element={<NameGeneratorList />} />
                <Route path='/dashboardrole' element={<DashboardRoleList />} />
                <Route path='/dashboardrole/:id' element={<DashboardRoleForm />} />
                <Route path='/playerrank' element={<PlayerRankList />} />
                <Route path='/playerrank/:id' element={<PlayerRankForm />} />
                <Route path='/blood/type' element={<BloodTypeList />} />
                <Route path='/blood/type/:id' element={<BloodTypeForm />} />
                <Route path='/player/import/donatorrank' element={<PlayerDonatorRankImport />} />
                <Route path='/player/import/clubplus' element={<PlayerClubPlusImport />} />
                <Route path='/player/import/donator' element={<PlayerDonatorImport />} />
                <Route path='/player/import/join_date' element={<PlayerJoinDateImport />} />
                <Route path='/player/import/chat_tag' element={<PlayerChatTagImport />} />
                <Route path='/player/import/tokens' element={<PlayerTokensImport />} />
                <Route path='/player/import/advanced' element={<PlayerAdvancedTokenImport />} />
                <Route path='/agesetting' element={<AgeSettingList />} />
                <Route path='/agesetting/:ageMonths' element={<AgeSettingForm />} />
                <Route path='/navigator' element={<NavigatorItemList />} />
                <Route path='/navigator/:id' element={<NavigatorItemForm />} />
                <Route path='/changepassword' element={<ChangePasswordPage />} />
                <Route path='/user/create-new' element={<CreateUserPage />} />
                <Route path='/log/chat' element={<ChatCommandLogs />} />
                <Route path='/log/command' element={<ChatCommandLogs />} />
                <Route path='/player/report' element={<PlayerReportList />} />
                <Route path='/player/ipcheck' element={<DuplicateIPList />} />
                <Route path='/tag' element={<TagList />} />
                <Route path='/tag/:id' element={<TagList />} />
                <Route path='/economy/values' element={<EconomyValues />} />
                <Route path='/player/feedback' element={<FeedbackList />} />
                <Route path='/player/export' element={<PlayerExport />} />
                <Route path='/preset-build/build' element={<PresetBuildList />} />
                <Route path='/preset-build/build/:id' element={<PresetBuildForm />} />
                <Route path='/preset-build/order' element={<PresetBuildOrderList />} />
                <Route path='/floating-text/' element={<FloatingTextList />} />
                <Route path='/floating-text/:id' element={<FloatingTextForm />} />
                <Route path='/website/files' element={<WebsiteFileManager />} />
                <Route path='/website/carousel' element={<WebsiteCarouselManager />}/>
                <Route path='/website/image/core' element={<WebsiteCoreImageManager />}/>
                <Route path='/horse' element={<HorseList />}/>
                <Route path='/horse/:id' element={<HorseForm />}/>
                <Route path='/player' element={<PlayerSearch />}/>
                <Route path='/monitoring' element={<Monitoring />}/>
                <Route path='/builder/server' element={<BuilderServerConsole />}/>
                <Route path='/bans' element={<Bans />}/>
                <Route path='/player/fine' element={<FineList />}/>
                <Route path='/log/xp' element={<XPLogViewer />}/>
                <Route path='/pregnancy' element={<PregnancySearch />}/>
                <Route path='/i18n-prefix' element={<I18nPrefixList />} />
                <Route path='/log/crash-reports' element={<CrashReportsViewer />} />
                <Route path='/log/forge' element={<ForgeLogsViewer />} />
                <Route path='/log/forge/latest' element={<ForgeLogsViewer />} />
                <Route path='/double-xp' element={<DoubleXpManager />} />
                <Route path='/roadmap' element={<RoadmapList />} />
                <Route path='/roadmap/:id' element={<RoadmapForm />} />
                <Route path='/badge' element={<BadgeList />} />
                <Route path='/badge/:id' element={<BadgeForm />} />
                <Route path='/player/token-balances' element={<TokenBalanceSearch />} />
                <Route path='/player/referral-reward' element={<PlayerReferralRewardList />} />
                <Route path='/medicine' element={<MedicineList />} />
                <Route path='/medicine/:id' element={<MedicineForm />} />
                <Route path='/medicineoverdosegroup' element={<MedicineOverdoseGroupList />} />
                <Route path='/medicineoverdosegroup/:id' element={<MedicineOverdoseGroupForm />} />
                <Route path='/provision' element={<ProvisionList />} />
                <Route path='/provision/:id' element={<ProvisionForm />} />
                <Route path='/coat' element={<CoatTextureList />} />
                <Route path='/coat/:layer/:texture' element={<CoatTextureForm />} />
                <Route path='/personality' element={<PersonalityList />} />
                <Route path='/personality/:id' element={<PersonalityForm />} />
                <Route path='/supplement' element={<SupplementList />} />
                <Route path='/supplement/:id' element={<SupplementForm />} />
                <Route path='/credit' element={<CreditList />} />
                <Route path='/credit/:id' element={<CreditForm />} />
            </Routes>
            </Suspense>
        </Box>
    );
};

const App = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);
    const authToken = useAuthToken();

    useEffect(() => {
        if (authToken !== null) {
            loadFeatureFlags();
        }
    }, []);

    const setBackgroundColor = (color) => {
        document.body.style.backgroundColor = color;
    };

    const setThemeColor = (color) => {
        document.querySelector('meta[name="theme-color"]').setAttribute('content', color);
    };

    useEffect(() => {
        if (authToken !== null) {
            setIsLoggedIn(true);
        } else {
            setIsLoggedIn(false);
            if (!location.pathname.startsWith('/login')) {
                const redirectTo = location.pathname + location.search;
                navigate(`/login?redirect=${encodeURIComponent(redirectTo)}`);
            }
        }
        setAuthChecked(true);
    }, [authToken, location.pathname, location.search, navigate]);

    useEffect(() => {
        if (authChecked) {
            const applyColors = () => {
                if (isLoggedIn) {
                    setBackgroundColor('white');
                    setThemeColor('#1976D2');
                } else {
                    setBackgroundColor('#adf1ff');
                    setThemeColor('#adf1ff');
                }
            };

            applyColors();
            // Re-apply shortly after to win against late meta/body updates on first paint.
            const delay = setTimeout(applyColors, 100);

            return () => clearTimeout(delay);
        }
    }, [isLoggedIn, authChecked]);

    const [isNavbarVisible, setIsNavbarVisible] = useState(false);

    const toggleNavbar = () => {
        setIsNavbarVisible(!isNavbarVisible);
    };

    const hideNavbar = () => {
        setIsNavbarVisible(false);
    };

    return (
        <AppContainer>
            <StatusProvider loggedIn={isLoggedIn}>
                <PlayerLocationProvider>
                    <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
                        <Navbar isVisible={isNavbarVisible} onHide={hideNavbar} />
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: '100%',
                            marginLeft: isNavbarVisible ? '250px' : 0,
                            transition: (theme) =>
                                theme.transitions.create('margin', {
                                    duration: 158,
                                    easing: 'linear',
                                }),
                        }}>
                            {isLoggedIn && <>
                                <Header toggleNavbar={toggleNavbar} />
                                <BreadcrumbTrail />
                            </>}
                            <ErrorBoundary showTryAgain showGoHome>
                                <AppRoutes />
                            </ErrorBoundary>
                            <ToastContainer
                                position="bottom-right"
                                autoClose={3000}
                                hideProgressBar={false}
                                newestOnTop={false}
                                closeOnClick
                                draggable
                                pauseOnHover
                                transition={Slide}
                                limit={5}
                                pauseOnFocusLoss={false}
                            />
                        </Box>
                    </Box>
                </PlayerLocationProvider>
            </StatusProvider>
        </AppContainer>
    );
};

export default App;
